import React, {useEffect, useMemo, useState} from "react";
import Title from "../dashboard/Title";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import TextInput from "../form/input/TextInput";
import ChipInput from "../form/input/ChipInput";
import Popup from "./Popup";
import ActionButton from "../form/input/ActionButton";
import Tooltip from "@material-ui/core/Tooltip";
import {Chip, InputAdornment} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import {Search} from "@material-ui/icons";
import ConfirmDialog from "./ConfirmDialog";
import useTable from "./useTable";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Container from "@material-ui/core/Container";
import {NotificationManager} from "react-notifications";
import {makeStyles} from "@material-ui/core/styles";
import UserService from "../../services/user.service";
import AuthService from "../../services/auth.service";
import {logOut} from "../../helpers/logout";
import {BRAND, useDashboardStyles} from "../settings/dashboardStyles";

const useLocalStyles = makeStyles(() => ({
    searchInput: {
        flex: "1 1 360px",
        minWidth: 240,
    },
    placesCell: {
        maxWidth: 520,
    },
    placesWrap: {
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
    },
    placeChip: {
        background: BRAND.creamSoft,
        border: `1px solid ${BRAND.goldSoft}`,
        color: BRAND.deep,
        fontWeight: 500,
        height: 24,
        "& .MuiChip-label": {fontSize: "0.78rem"},
    },
    placeCount: {
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 9px",
        background: BRAND.goldBg,
        color: BRAND.deep,
        borderRadius: 999,
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: 0.6,
        border: `1px solid ${BRAND.goldSoft}`,
    },
    actionsCell: {
        whiteSpace: "nowrap",
        width: 1,
    },
    actionsRow: {
        display: "inline-flex",
        flexWrap: "nowrap",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 6,
    },
}));

const DEFAULT_VALUES = {_id: null, office: "", places: []};

export default function RegistryOfficeList(props) {
    const classes = useDashboardStyles();
    const localClasses = useLocalStyles();

    const [items, setItems] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [values, setValues] = useState(DEFAULT_VALUES);
    const [errors, setErrors] = useState({});
    const [show, setShow] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({isOpen: false, title: "", subTitle: ""});

    const isAdmin = useMemo(() => {
        const user = AuthService.getCurrentUser();
        return Boolean(user && Array.isArray(user.roles) && user.roles.includes("ROLE_ADMIN"));
    }, []);

    const placeSuggestions = useMemo(() => {
        const set = new Set();
        items.forEach((it) => (it.places || []).forEach((p) => set.add(p)));
        return Array.from(set).sort((a, b) => a.localeCompare(b, "sk"));
    }, [items]);

    const filterFn = useMemo(() => {
        const search = searchText.trim().toLowerCase();
        return {
            fn: (rows) => {
                if (!search) return rows;
                return rows.filter((row) => {
                    const office = (row.office || "").toLowerCase();
                    if (office.includes(search)) return true;
                    return (row.places || []).some((p) => (p || "").toLowerCase().includes(search));
                });
            },
        };
    }, [searchText]);

    const headCells = useMemo(() => {
        const base = [
            {id: "office", label: "Matričný úrad"},
            {id: "places", label: "Územný obvod (obce / mestské časti)", disableSorting: true},
            {id: "placesCount", label: "Počet", disableSorting: true, align: "right"},
        ];
        if (isAdmin) {
            base.push({id: "actions", label: "Akcie", disableSorting: true, align: "right"});
        }
        return base;
    }, [isAdmin]);

    const {TblContainer, TblHead, TblPagination, recordsAfterPagingAndSorting} = useTable(items, headCells, filterFn);

    const errNotification = (e) => {
        if (e && e.response && e.response.status === 401) {
            logOut(props);
            NotificationManager.error("Musíte sa nanovo prihlásiť.", "Chyba", 5000);
        } else {
            NotificationManager.error(
                "Došlo k neočakávanej chybe. Skúste neskôr alebo kontaktujte podporu.",
                "Chyba",
                5000
            );
        }
    };

    const fetchAll = () => {
        UserService.getRegistryOffices().then(
            (response) => {
                setItems(response.data || []);
            },
            (e) => {
                if (e && e.response && e.response.status === 401) logOut(props);
                else errNotification(e);
            }
        );
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resetToDefault = () => {
        setValues(DEFAULT_VALUES);
        setErrors({});
        setShow(false);
    };

    const handleChange = (event) => {
        const {name, value} = event.target;
        setValues((prev) => ({...prev, [name]: value}));
    };

    const handlePlacesChange = (next) => {
        setValues((prev) => ({...prev, places: next}));
    };

    const validate = () => {
        const next = {};
        next.office = values.office && values.office.trim() ? "" : "Vyplňte názov matričného úradu.";
        setErrors(next);
        return Object.values(next).every((x) => x === "");
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) {
            NotificationManager.error("Skontrolujte vyplnený formulár!", "Chyba", 5000);
            return;
        }

        const payload = {
            _id: values._id,
            office: values.office.trim(),
            places: (values.places || []).map((p) => p.trim()).filter(Boolean),
        };

        const onSuccess = (created) => {
            NotificationManager.success(
                values._id ? "Záznam upravený!" : "Záznam pridaný!",
                values._id ? "Upravené" : "Vytvorené",
                5000
            );
            resetToDefault();
            fetchAll();
            return created;
        };

        if (values._id) {
            UserService.updateRegistryOffice(payload).then(onSuccess, errNotification);
        } else {
            UserService.addRegistryOffice(payload).then(onSuccess, errNotification);
        }
    };

    const handleEdit = (row) => () => {
        setValues({_id: row._id, office: row.office || "", places: [...(row.places || [])]});
        setErrors({});
        setShow(true);
    };

    const handleDelete = (id) => {
        setConfirmDialog({...confirmDialog, isOpen: false});
        UserService.deleteRegistryOffice(id).then(
            () => {
                NotificationManager.success("Záznam vymazaný!", "Vymazané", 5000);
                fetchAll();
            },
            (e) => {
                errNotification(e);
            }
        );
    };

    return (
        <Container maxWidth="lg" className={classes.pageContainer}>
            <Paper className={classes.paper} elevation={0}>
                <div className={classes.sectionHeader}>
                    <Title>Matrika — územné obvody matričných úradov</Title>
                    <div className={classes.sectionToolbar}>
                        <TextInput
                            className={localClasses.searchInput}
                            label="Vyhľadať"
                            placeholder="Matričný úrad alebo obec"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small"/>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {isAdmin && (
                            <Button
                                startIcon={<AddIcon/>}
                                className={classes.primaryAction}
                                onClick={() => {
                                    setValues(DEFAULT_VALUES);
                                    setErrors({});
                                    setShow(true);
                                }}
                            >
                                Pridať záznam
                            </Button>
                        )}
                    </div>
                </div>

                {items.length === 0 ? (
                    <div className={classes.emptyState}>
                        Zatiaľ nie sú nahraté žiadne matričné úrady.
                    </div>
                ) : (
                    <>
                        <TblContainer>
                            <TblHead/>
                            <TableBody>
                                {recordsAfterPagingAndSorting().map((item) => (
                                    <TableRow key={item._id}>
                                        <TableCell>
                                            <strong>{item.office}</strong>
                                        </TableCell>
                                        <TableCell className={localClasses.placesCell}>
                                            <div className={localClasses.placesWrap}>
                                                {(item.places || []).map((p, idx) => (
                                                    <Chip
                                                        key={`${item._id}-${idx}-${p}`}
                                                        size="small"
                                                        label={p}
                                                        className={localClasses.placeChip}
                                                    />
                                                ))}
                                            </div>
                                        </TableCell>
                                        <TableCell align="right">
                                            <span className={localClasses.placeCount}>{(item.places || []).length}</span>
                                        </TableCell>
                                        {isAdmin && (
                                            <TableCell align="right" className={localClasses.actionsCell}>
                                                <div className={localClasses.actionsRow}>
                                                    <ActionButton color="primary">
                                                        <Tooltip title="Upraviť záznam">
                                                            <EditIcon onClick={handleEdit(item)} fontSize="small"/>
                                                        </Tooltip>
                                                    </ActionButton>
                                                    <ActionButton color="secondary">
                                                        <Tooltip title="Vymazať záznam">
                                                            <DeleteIcon
                                                                fontSize="small"
                                                                onClick={() =>
                                                                    setConfirmDialog({
                                                                        isOpen: true,
                                                                        title: "Naozaj chcete vymazať tento záznam?",
                                                                        subTitle: "Túto operáciu nevieš vrátiť.",
                                                                        onConfirm: () => handleDelete(item._id),
                                                                    })
                                                                }
                                                            />
                                                        </Tooltip>
                                                    </ActionButton>
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </TblContainer>
                        <TblPagination/>
                    </>
                )}

                <Popup
                    title={values._id ? "Úprava záznamu" : "Pridanie záznamu"}
                    openPopup={show && isAdmin}
                    setOpenPopup={setShow}
                    additionalWork={resetToDefault}
                    maxW="md"
                    actions={
                        <Button
                            variant="contained"
                            type="submit"
                            form="registry-office-form"
                            className={classes.primaryAction}
                        >
                            {values._id ? "Uložiť zmeny" : "Pridať záznam"}
                        </Button>
                    }
                >
                    <form id="registry-office-form" autoComplete="off" onSubmit={handleSubmit}>
                        <div className={classes.formField}>
                            <TextInput
                                label="Matričný úrad"
                                placeholder="Napr. Budmerice"
                                error={errors.office}
                                name="office"
                                value={values.office || ""}
                                onChange={handleChange}
                                fullWidth
                            />
                        </div>
                        <div className={classes.formField}>
                            <ChipInput
                                label="Územný obvod (obce / mestské časti)"
                                placeholder="Napíšte obec a stlačte Enter alebo čiarku"
                                value={values.places || []}
                                onChange={handlePlacesChange}
                                options={placeSuggestions}
                                helperText="Pridávajte obce po jednej cez Enter / čiarku. Bubliny vymažete krížikom."
                            />
                        </div>
                    </form>
                </Popup>

                <ConfirmDialog
                    confirmDialog={confirmDialog}
                    setConfirmDialog={setConfirmDialog}
                    icon={<DeleteIcon/>}
                />
            </Paper>
        </Container>
    );
}
